import { useState, useEffect } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { getTeacherCohorts, getTeacherStudentProfiles } from '@/lib/db'
import type { Cohort, Profile } from '@/types'
import {
  Users,
  Search,
  Filter,
  UsersRound,
  User,
  Loader2,
  Mail,
  Eye
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { cn } from '@/lib/utils'

export default function Students() {
  const { profile } = useAuth()
  const [searchTerm, setSearchTerm] = useState('')
  const [cohortFilter, setCohortFilter] = useState<string>('all')
  const [selectedStudent, setSelectedStudent] = useState<string | null>(null)

  const [cohorts, setCohorts] = useState<Cohort[]>([])
  const [studentProfiles, setStudentProfiles] = useState<Profile[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!profile?.id) return

    async function loadData() {
      setLoading(true)
      const [cohortsRes, studentsRes] = await Promise.all([
        getTeacherCohorts(profile!.id),
        getTeacherStudentProfiles(profile!.id)
      ])

      if (cohortsRes.data) setCohorts(cohortsRes.data)
      if (studentsRes.data) setStudentProfiles(studentsRes.data)
      setLoading(false)
    }

    loadData()
  }, [profile?.id])

  // Mapear student IDs por cohorte para filtros
  const getStudentCohorts = (studentId: string): Cohort[] => {
    return cohorts.filter(c => (c.student_ids || []).includes(studentId))
  }

  const filteredStudents = studentProfiles.filter(student => {
    const matchesSearch = `${student.first_name} ${student.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         student.email.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesCohort = cohortFilter === 'all' || getStudentCohorts(student.id).some(c => c.id === cohortFilter)
    return matchesSearch && matchesCohort
  })

  const selectedStudentData = studentProfiles.find(s => s.id === selectedStudent)

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Mis Estudiantes</h1>
        <p className="text-muted-foreground">
          Sigue el progreso de tus estudiantes
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <div className="bg-card rounded-xl p-4 border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{studentProfiles.length}</p>
              <p className="text-xs text-muted-foreground">
                Estudiantes total
              </p>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-xl p-4 border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <UsersRound className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{cohorts.length}</p>
              <p className="text-xs text-muted-foreground">
                Cohortes
              </p>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-xl p-4 border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">
                {studentProfiles.filter(s => s.is_active).length}
              </p>
              <p className="text-xs text-muted-foreground">
                Activos
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Buscar estudiante..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg bg-background"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <select
            value={cohortFilter}
            onChange={(e) => setCohortFilter(e.target.value)}
            className="px-3 py-2 border rounded-lg bg-background"
          >
            <option value="all">Todas las cohortes</option>
            {cohorts.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Layout con lista y detalle */}
      <div className="grid md:grid-cols-3 gap-6">
        {/* Lista de estudiantes */}
        <div className="md:col-span-2 space-y-3">
          {filteredStudents.length === 0 ? (
            <div className="text-center py-12 bg-card rounded-xl border">
              <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-lg font-medium">
                No se encontraron estudiantes
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                Los estudiantes asignados a tus cohortes aparecerán aquí
              </p>
            </div>
          ) : (
            filteredStudents.map((student) => {
              const studentCohorts = getStudentCohorts(student.id)
              return (
                <button
                  key={student.id}
                  onClick={() => setSelectedStudent(student.id)}
                  className={cn(
                    'w-full flex items-center gap-4 p-4 bg-card rounded-xl border transition-colors text-left',
                    selectedStudent === student.id
                      ? 'border-primary bg-primary/5'
                      : 'hover:border-primary/50'
                  )}
                >
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                    {student.avatar_url ? (
                      <img src={student.avatar_url} alt="" className="w-full h-full rounded-full object-cover" />
                    ) : (
                      <User className="w-6 h-6 text-primary" />
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="font-semibold truncate">{student.first_name} {student.last_name}</h3>
                      {!student.is_active && (
                        <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs font-medium">
                          Inactivo
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground truncate">{student.email}</p>
                    {studentCohorts.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {studentCohorts.map(c => (
                          <span key={c.id} className="px-2 py-0.5 bg-muted rounded text-xs">
                            {c.name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </button>
              )
            })
          )}
        </div>

        {/* Panel de detalle */}
        <div className="bg-card rounded-xl border p-6">
          {selectedStudentData ? (
            <div className="space-y-6">
              <div className="text-center">
                <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  {selectedStudentData.avatar_url ? (
                    <img src={selectedStudentData.avatar_url} alt="" className="w-full h-full rounded-full object-cover" />
                  ) : (
                    <User className="w-10 h-10 text-primary" />
                  )}
                </div>
                <h2 className="text-xl font-bold">{selectedStudentData.first_name} {selectedStudentData.last_name}</h2>
                <p className="text-muted-foreground">{selectedStudentData.email}</p>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between py-3 border-b">
                  <span className="text-muted-foreground">
                    Rol
                  </span>
                  <span className="font-semibold capitalize">{selectedStudentData.role}</span>
                </div>
                <div className="flex items-center justify-between py-3 border-b">
                  <span className="text-muted-foreground">
                    Idioma
                  </span>
                  <span className="font-semibold">{selectedStudentData.preferred_language === 'fr' ? 'Français' : 'Español'}</span>
                </div>
                <div className="flex items-center justify-between py-3 border-b">
                  <span className="text-muted-foreground">
                    Cohortes
                  </span>
                  <span className="font-semibold">{getStudentCohorts(selectedStudentData.id).length}</span>
                </div>
                <div className="flex items-center justify-between py-3 border-b">
                  <span className="text-muted-foreground">
                    Inscrito el
                  </span>
                  <span className="font-semibold">{new Date(selectedStudentData.created_at).toLocaleDateString()}</span>
                </div>
                <div className="flex items-center justify-between py-3">
                  <span className="text-muted-foreground">
                    Estado
                  </span>
                  <span className={cn(
                    'px-2 py-1 rounded-full text-xs font-medium',
                    selectedStudentData.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  )}>
                    {selectedStudentData.is_active ? 'Activo' : 'Inactivo'}
                  </span>
                </div>
              </div>

              <Link
                to={`/teacher/students/${selectedStudentData.id}`}
                className="flex items-center justify-center gap-2 w-full px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
              >
                <Eye className="w-4 h-4" />
                Ver progreso completo
              </Link>

              <a
                href={`mailto:${selectedStudentData.email}`}
                className="flex items-center justify-center gap-2 w-full px-4 py-2 border text-foreground rounded-lg hover:bg-muted transition-colors"
              >
                <Mail className="w-4 h-4" />
                Enviar email
              </a>
            </div>
          ) : (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">
                Selecciona un estudiante para ver sus detalles
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
